import { BrowserRouter as Router } from "react-router-dom";
import { useSelector } from "react-redux";
import {
  GoabOneColumnLayout,
  GoabMicrositeHeader,
  GoabAppHeader,
  GoabAppFooter,
  GoabCircularProgress,
  GoabNotification,
} from "@abgov/react-components";
import { AppRouter } from "./Components/Router/AppRouter/AppRouter";
import { cleanupDomainKeyFromUrl, envType, initProxySession } from "./MPP-Common-SPA/utils/globalUtility";
import { SettingNames } from "./MPP-Common-SPA/programSettings/enums";
import { selectProgramName, selectProgramId } from "./Store/api/program/programSelectors";
import HeaderComponent from "./PortalRunner/components/Header/HeaderComponent";
import { useGetSessionQuery, useGetRefDataListQuery, useGetDynamicFormListQuery, useGetProgramQuery, useGetProgramEventListQuery, useGetProgramSettingBySettingNameQuery, useGetScheduledMaintenanceNotificationQuery, useGetScheduledMaintenanceSupportInformationQuery } from "./Store/api/apiSlice";
import { selectIsLoggedIn } from "./Store/api/auth/authSelectors";
import { useGetApplicationListQuery } from "./Store/api/application/applicationApi";
import { UnsavedChangesProvider } from "./MPP-Common-SPA/common/unsavedChangesContext";
import { GoogleMapsProvider } from "./MPP-Common-SPA/common/GoogleMapsContext";
import { useEffect, useMemo, useState } from "react";
import DOMPurify from "dompurify";
import { GoabServiceLevel } from "@abgov/ui-components-common";
import ToastNotifications from "./Components/Notifications/ToastNotifications";
import { initializeAnalytics } from "./MPP-Common-SPA/utils/analytics";
import { useSessionLock } from "./PortalRunner/hooks/useSessionLock";
import { SessionRefreshModal } from "./Components/Notifications/SessionRefreshModal";
import NotFound from "./Components/ErrorPages/NotFound";

const App = () => {
  const isLoggedIn = useSelector(selectIsLoggedIn);
  const programName = useSelector(selectProgramName);
  const programId = useSelector(selectProgramId);
  const isSessionLocked = useSessionLock(); // hook
  const { data: scheduledMaintenanceNotification, isLoading: scheduledMaintenanceNotificationIsLoading } = useGetScheduledMaintenanceNotificationQuery();
  const { data: scheduledMaintenanceSupportInformation, isLoading: scheduledMaintenanceSupportInformationIsLoading } = useGetScheduledMaintenanceSupportInformationQuery(undefined, {
    skip: !scheduledMaintenanceNotification?.isEnabled,
  });
  const [appHeaderRenderKey, setAppHeaderRenderKey] = useState<number>(0);

  useEffect(() => {
    initProxySession(); 
  }, []);

  const { data: programDisplayNameSetting, isLoading: isProgramSettingLoading, isSuccess: isProgramSettingLoaded } = useGetProgramSettingBySettingNameQuery(
    { settingName: SettingNames.ProgramDisplayName, programId },
    { skip: !programId }
  );
  
  useGetApplicationListQuery(undefined, { skip: !isLoggedIn });
  const { isLoading: isRefDataLoading, isSuccess: refDataLoaded } = useGetRefDataListQuery();
  const { isLoading: isDynamicFormsLoading, isSuccess: dynamicFormsLoaded } = useGetDynamicFormListQuery();
  const { isLoading: isProgramLoading, isSuccess: programLoaded } = useGetProgramQuery();
  const { isLoading: isSessionLoading, isSuccess: sessionLoaded } = useGetSessionQuery(undefined, {
    refetchOnFocus: true,
    refetchOnReconnect: true,
  });
  const { isLoading: isEventsLoading, isSuccess: isEventsLoaded } = useGetProgramEventListQuery(programId, {
    skip: !programId,
  });

  useEffect(() => {
    if (programLoaded) {
      cleanupDomainKeyFromUrl();
    }
  }, [programLoaded]);

  // Initialize Analytics on mount
  useEffect(() => {
    // This is temporary. We should get the measurementId from the DB based on what program the application belongs to.
    const measurementId = process.env.REACT_APP_GA_MEASUREMENT_ID;
    initializeAnalytics(measurementId);
  }, []);

  // GOA AppHeader resize fix:
  // GOA AppHeader resize fix:
  // When crossing desktop/tablet/mobile breakpoints with menu open, GOA header internal state
  // can get stale and hide the menu button visually. Remount on breakpoint changes to reset it.
  // Guard against environments where window.matchMedia is unavailable (e.g. Jest/jsdom, SSR).
  useEffect(() => {
    if (typeof window === "undefined" || typeof window.matchMedia !== "function") return;

    const mobileMq = window.matchMedia("(max-width: 623px)");
    const tabletMq = window.matchMedia("(min-width: 624px) and (max-width: 1023px)");

    const getViewportMode = () => {
      if (mobileMq.matches) return "mobile";
      if (tabletMq.matches) return "tablet";
      return "desktop";
    };

    let previousViewportMode = getViewportMode();

    const handleBreakpointChange = () => {
      const currentViewportMode = getViewportMode();
      if (currentViewportMode !== previousViewportMode) {
        previousViewportMode = currentViewportMode;
        setAppHeaderRenderKey((prev) => prev + 1);
      }
    };

    if (mobileMq.addEventListener && tabletMq.addEventListener) {
      mobileMq.addEventListener("change", handleBreakpointChange);
      tabletMq.addEventListener("change", handleBreakpointChange);
    } else {
      mobileMq.addListener(handleBreakpointChange);
      tabletMq.addListener(handleBreakpointChange);
    }

    return () => {
      if (mobileMq.removeEventListener && tabletMq.removeEventListener) {
        mobileMq.removeEventListener("change", handleBreakpointChange);
        tabletMq.removeEventListener("change", handleBreakpointChange);
      } else {
        mobileMq.removeListener(handleBreakpointChange);
        tabletMq.removeListener(handleBreakpointChange);
      }
    };
  }, []);
  
  // citizen-facing program display name from program setting
  const [programDisplayName, setProgramDisplayName] = useState<string>("");
  useEffect(() => {
    if (programDisplayNameSetting?.configuration && programDisplayNameSetting.isEnabled) {
      try {
        const config = JSON.parse(programDisplayNameSetting.configuration);
        setProgramDisplayName(config?.DisplayName || "");
      } catch {
        setProgramDisplayName("");
      }
    } else {
      setProgramDisplayName("");
    }
  }, [programDisplayNameSetting]);

  // Combine loading states
  const isLoading = isSessionLoading || isRefDataLoading || isDynamicFormsLoading || isProgramLoading || isEventsLoading || isProgramSettingLoading;
  const isSuccess = sessionLoaded && refDataLoaded && dynamicFormsLoaded && programLoaded && isEventsLoaded && (isProgramSettingLoaded || !programId);

  const scheduledMaintenanceNotificationHtml = useMemo(() => {
    const supportInfoHtml = (scheduledMaintenanceSupportInformation?.configuration.Content ?? "")
      // Strip surrounding tags before inserting into main notification
      .replace(/^<p[^>]*>|<\/p>$/gi, "");
    const html = (scheduledMaintenanceNotification?.configuration.Content ?? "")
      .replace(/<\/p>$/, ` ${supportInfoHtml}</p>`);
    return DOMPurify.sanitize(html);
  }, [scheduledMaintenanceNotification?.configuration, scheduledMaintenanceSupportInformation?.configuration]);

  const isScheduledMaintenanceNotificationReady =
    !scheduledMaintenanceNotificationIsLoading &&
    !scheduledMaintenanceSupportInformationIsLoading &&
    scheduledMaintenanceNotificationHtml.trim() !== "";

  return (
    <Router>
      <GoogleMapsProvider>
        <UnsavedChangesProvider isLoggedIn={isLoggedIn}>
          <GoabOneColumnLayout>
            <section slot="header">
              <GoabMicrositeHeader type={envType as GoabServiceLevel} feedbackUrlTarget="blank" headerUrlTarget="blank"></GoabMicrositeHeader>
              <GoabAppHeader key={`goa-app-header-${appHeaderRenderKey}`} url="/?action=clear" heading={programDisplayName !== "" ? programDisplayName : (!isLoading ? programName : "")}>
                <HeaderComponent />
              </GoabAppHeader>
            </section>
            { scheduledMaintenanceNotification?.isEnabled && isScheduledMaintenanceNotificationReady &&
              <section className="scheduled-maintenance-notification">
                <GoabNotification type="important">
                  <span dangerouslySetInnerHTML={{ __html: scheduledMaintenanceNotificationHtml }} />
                </GoabNotification>
              </section>
            }
            <section>
              <ToastNotifications />
              <GoabCircularProgress
                variant="fullscreen"
                visible={isLoading}
                message="Loading..."
              />
              {isSuccess ? <AppRouter /> : !isLoading && <NotFound />}
            </section>
            <section slot="footer">
              <GoabAppFooter />
            </section>
          </GoabOneColumnLayout>
        </UnsavedChangesProvider>
      </GoogleMapsProvider>
      <SessionRefreshModal isOpen={isSessionLocked} />
    </Router>
  );
};

export default App;
